import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  Renderer2,
  signal,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SafeValue } from '@angular/platform-browser';
import { L10N_LOCALE, L10nTranslatePipe } from 'angular-l10n';
import { HtmlHelper } from '../html-helper/html-helper';
import { LogHelper } from '../logging/log-helper';

/**
 * Displays HTML content in a dynamically-sized iframe.
 *
 * The iframe automatically resizes to fit its content, constrained between
 * `defaultIframeHeight` (minimum) and `maxAutoIframeHeight` (maximum).
 * Users can manually expand beyond the max using the expand/reset controls.
 *
 * @example
 * ```html
 * <app-rich-content
 *   [richContent]="htmlString"
 *   [maxAutoIframeHeight]="300"
 *   [defaultIframeHeight]="100">
 * </app-rich-content>
 * ```
 */
@Component({
  selector: 'app-rich-content',
  templateUrl: './rich-content.component.html',
  styleUrls: ['./rich-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, L10nTranslatePipe],
})
export class RichContentComponent implements OnDestroy {
  readonly locale = inject(L10N_LOCALE);
  private readonly htmlHelper = inject(HtmlHelper);
  private readonly renderer2 = inject(Renderer2);
  private readonly log = new LogHelper('RichContent');
  private readonly iframe = viewChild<ElementRef<HTMLIFrameElement>>('richContentIframe');

  /** Maximum height the iframe will auto-resize to. User can expand beyond this manually. */
  readonly maxAutoIframeHeight = input<number>(256);

  /** Minimum height for the iframe, used when content is smaller or not yet measured. */
  readonly defaultIframeHeight = input<number>(128);

  /** Raw HTML content to display. Will be sanitized via HtmlHelper before rendering. */
  readonly richContent = input<string>();

  /** Current height of the iframe element in pixels. */
  readonly iframeHeight = signal<number>(this.defaultIframeHeight());

  /** Actual content height inside the iframe, or undefined if not yet measured. */
  readonly iframeContentHeight = signal<number | undefined>(undefined);

  /** Sanitized HTML content safe for binding to the iframe. */
  readonly safeRichContent = signal<SafeValue | null>(null);

  /**
   * Whether to show expand/collapse controls.
   * True when content exceeds displayed height OR when frame is expanded beyond max.
   */
  readonly showExpandControls = computed(() => {
    const contentHeight = this.iframeContentHeight();
    const frameHeight = this.iframeHeight();
    const maxHeight = this.maxAutoIframeHeight();

    const showMoreDiff = (contentHeight ?? 0) - (frameHeight ?? 0);
    const showLessDiff = (frameHeight ?? 0) - (maxHeight ?? 0);
    if (isNaN(showMoreDiff) && isNaN(showLessDiff)) return false;
    return !contentHeight || showMoreDiff > 0 || showLessDiff > 0;
  });

  private removeListener: () => void = () => {};

  constructor() {
    // Watch for richContent changes and sanitize
    effect(() => {
      const content = this.richContent();
      if (content) {
        this.safeRichContent.set(this.htmlHelper.processRichContent(content));
      } else {
        this.safeRichContent.set(null);
      }
    });

    // Watch for iframe to become available after content is set
    effect(() => {
      const iframe = this.iframe()?.nativeElement;
      const content = this.safeRichContent();

      if (content && iframe) {
        this.removeListener();
        this.removeListener = this.renderer2.listen(iframe, 'load', () => {
          requestAnimationFrame(() => this.recalculateHeight(iframe, 'onload'));
        });
        requestAnimationFrame(() => this.recalculateHeight(iframe, 'iframe effect'));
      } else if (!content) {
        this.removeListener();
      }
    });

    // Watch for defaultIframeHeight changes from parent
    effect(() => {
      const defaultHeight = this.defaultIframeHeight();
      if (!this.iframeContentHeight()) {
        this.iframeHeight.set(defaultHeight);
      }
    });
  }

  ngOnDestroy(): void {
    this.removeListener();
  }

  /** Returns the native iframe element, if available. */
  getFrame(): HTMLIFrameElement | undefined {
    return this.iframe()?.nativeElement;
  }

  /** Recalculates iframe height on window resize. */
  @HostListener('window:resize', ['$event.target'])
  onResize(_target: unknown) {
    requestAnimationFrame(() => this.recalculateHeight(this.getFrame(), 'onresize'));
  }

  /** Measures and stores the iframe's content height. */
  setContentHeight(iframe: HTMLIFrameElement | undefined) {
    this.iframeContentHeight.set(iframe?.contentWindow?.document?.documentElement?.scrollHeight);
  }

  /**
   * Measures content and updates iframe height within constraints.
   * @param iframe - The iframe element to measure
   * @param source - Debug identifier for logging (e.g., 'onload', 'onresize')
   */
  recalculateHeight(iframe: HTMLIFrameElement | undefined, source: string) {
    this.setContentHeight(iframe);
    const contentHeight = this.iframeContentHeight();
    const defaultHeight = this.defaultIframeHeight();
    const maxHeight = this.maxAutoIframeHeight();
    const effectiveHeight = Math.min(Math.max(contentHeight, defaultHeight), maxHeight);
    this.iframeHeight.set(effectiveHeight);

    const extra = false; // enable for extra log output (debug resizing, etc)
    if (extra) this.log.debug(
      'recalculateHeight()',
      'source:', source,
      'contentHeight:', contentHeight,
      'effectiveHeight:', effectiveHeight
    );
  }

  /** Doubles the iframe height, capped at the content height. */
  expandFrame() {
    const currentHeight = this.iframeHeight();
    const contentHeight = this.iframeContentHeight();
    let newHeight = currentHeight * 2;
    if (contentHeight && newHeight > contentHeight) {
      newHeight = contentHeight;
    }
    this.iframeHeight.set(newHeight);
  }

  /** Resets the iframe height to the maximum auto-height. */
  resetFrame() {
    this.iframeHeight.set(this.maxAutoIframeHeight());
  }
}
